import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {navigate} from 'gatsby';
import styled, {css, keyframes} from 'styled-components';
import {Padding, p, py, pl, pt} from 'styled-components-spacing';
import breakpoint from 'styled-components-breakpoint';

import colors from '@nib-components/colors';
import {Stack} from '@nib/layout';
import {TickSystemIcon} from '@nib/icons';
import Heading from '@nib-components/heading';
import Copy from '@nib-components/copy';
import Link from '@nib-components/link';

import {colorTrueGreen} from '@nib-components/theme';
import CheckDetailsImg from '../../img/first_choice_check_details.jpg';
import GoodToGoImg from '../../img/first_choice_good_to_go.jpg';
import InterestedImg from '../../img/first_choice_interested.jpg';

const defaultFeatures = [
  {
    heading: 'Check your details on Healthpoint ',
    content: function CheckDetails() {
      return (
        <div>
          We work with Healthpoint to make it easy for you to check and update your details via{' '}
          <Link href="https://healthpoint.co.nz" target="_blank">
            healthpoint.co.nz
          </Link>
          .
        </div>
      );
    },
    image: CheckDetailsImg
  },
  {
    heading: "Tell us you're interested in joining the First Choice network",
    content: function Interested() {
      return (
        <div>
          Complete the <Link onClick={() => navigate('/registration-form/')}>registration form</Link>
          <sup>2</sup> and we’ll be in touch.
        </div>
      );
    },
    image: InterestedImg
  },
  {
    heading: 'You’re good to go',
    content: function GoodToGo() {
      return (
        <div>
          Once we’ve confirmed your eligibility, we’ll email you an invitation to setup your Health Partner website account<sup>3</sup> so you can start submitting pre-approvals and claims for nib and
          AA Health members.
        </div>
      );
    },
    image: GoodToGoImg
  }
];

const Wrapper = styled.div`
  ${py(6)};
  display: grid;

  ${breakpoint('lg')`
    grid-template-columns: 1fr 1fr;
  `};
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${pt(6)};

  ${breakpoint('lg')`
    ${pt(0)};
    ${pl(8)};
  `};
`;

const FeatureBlock = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  ${p(4)};
  cursor: pointer;

  border-left: 2px solid ${props => (props.isActive ? colorTrueGreen : colors.sneezy)};
  color: ${props => (props.isActive ? colors.doc : colors.dopey)};
`;

const CustomTick = styled(TickSystemIcon)`
  ${p(2)};
  border: 2px solid transparent;

  color: ${colorTrueGreen};
  ${props =>
    props.isActive &&
    css`
      border-color: ${colorTrueGreen};
      border-radius: 50%;
    `}

  transform: translateY(-0.5rem);
`;

const fadeInAnimation = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const fadeOutAnimation = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

const ImageWrapper = styled.div`
  min-height: 200px;
  width: 100%;
  overflow: hidden;
  position: relative;

  ${breakpoint('md')`
    min-height: 300px;
  `};

  ${breakpoint('lg')`
    height: 100%;
    width: auto;
  `};
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  position: absolute;
  opacity: 0;

  animation-name: ${fadeOutAnimation};
  animation-duration: 1s;
  animation-iteration-count: 1;

  ${breakpoint('lg')`
    height: 100%;
    width: auto;
  `};

  ${props =>
    props.isActive &&
    css`
      opacity: 1;
      animation-name: ${fadeInAnimation};
      animation-duration: 1s;
      animation-iteration-count: 1;
    `}
`;

const VerticalFeaturePanel = ({title, features}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeIndex < defaultFeatures.length - 1) {
        const newIndex = activeIndex + 1;
        setActiveIndex(newIndex);
      } else {
        setActiveIndex(0);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeIndex]);

  return (
    <Wrapper>
      <ImageWrapper>
        {features &&
          features.map((feature, index) => {
            return <Image key={index} src={feature.image} isActive={index === activeIndex} />;
          })}
      </ImageWrapper>
      <ContentWrapper>
        {title && (
          <Padding bottom="6">
            <Heading color="trueGreen" size={{sm: 4, md: 3, lg: 2}} component="h2">
              Become a First Choice health partner
            </Heading>
          </Padding>
        )}
        {features &&
          features.map((feature, index) => {
            return (
              <FeatureBlock key={index} isActive={index === activeIndex} onClick={() => setActiveIndex(index)}>
                <CustomTick isActive={index === activeIndex} />
                <Padding left="4">
                  <Stack space={3}>
                    <Heading color="trueGreen" size={3}>
                      {feature.heading}
                    </Heading>
                    <Copy color="darkest">{feature.content()}</Copy>
                  </Stack>
                </Padding>
              </FeatureBlock>
            );
          })}
      </ContentWrapper>
    </Wrapper>
  );
};

VerticalFeaturePanel.propTypes = {
  title: PropTypes.string,
  features: PropTypes.array
};

VerticalFeaturePanel.defaultProps = {
  title: 'How to become an nib Health Partner',
  features: defaultFeatures
};

VerticalFeaturePanel.displayName = 'VerticalFeaturePanel';

export default VerticalFeaturePanel;
